import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@mui/material';
import DetailsBookModal from '../Screens/Books/DetailsBookModal/DetailsBookModal';
import HelmetMetaData from '../HelmetMetaData/HelmetMetaData';
import { setLoading } from '../../slices/feedbackSlice';
import Collection from '../Collection/Collection';
import Search from '../Search/Search';
import { spinner } from '../Layout/Spinner/Spinner';
import { getSharedCategories, getSharedCollection } from '../../slices/itemsSlice';

const useStyles = makeStyles()((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
  },
  button: {
    width: '281.25px',
  },
  content: {
    display: 'flex',
    justifyContent: 'center',
  },
}));

export default function Shared() {
  const { classes } = useStyles();
  const location = useLocation().pathname;
  const dispatch = useDispatch();
  const [detailsBookModalOpen, setDetailsBookModalOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState({});
  const [filters, setFilters] = useState({
    page: 1,
    query: '',
    available: false,
    categoryIds: [],
  });
  const [emptyListFlag, setEmptyListFlag] = useState(false);
  const [t] = useTranslation('myBooks');

  const collectionSlug = location.split('/').pop();

  const loading = useSelector((state) => state.feedback.loading);

  const categories = useSelector((state) => state.items.categories);
  const books = useSelector((state) => state.items.sharedCollection.books);
  const collection = useSelector((state) => state.items.sharedCollection || []);
  const notFound = !!collection.notFound;

  useEffect(() => {
    dispatch(getSharedCategories({ slug: collectionSlug }));
  }, [dispatch, collectionSlug]);

  useEffect(() => {
    dispatch(setLoading(true));
    dispatch(getSharedCollection({ slug: collectionSlug, filters }))
      .unwrap()
      .then(() => dispatch(setLoading(false)))
      .catch((err) => {
        console.log(err);
        dispatch(setLoading(false));
      });
  }, [filters, dispatch, collectionSlug]);

  useEffect(() => {
    if (!books.results.length) {
      setEmptyListFlag(true);
    } else {
      setEmptyListFlag(false);
    }
  }, [books]);

  const handleOpenBookModal = (event, book) => {
    setSelectedBook(book);
    setDetailsBookModalOpen(true);
  };

  const handleCloseBookModal = () => {
    setDetailsBookModalOpen(false);
    setSelectedBook({});
  };

  const handleSearch = (query) => {
    const newFilters = { ...filters, query, page: 1 };
    setFilters(newFilters);
  };

  const handleOnFilterCategories = (event, categoriesQuery) => {
    const categoryIds = categoriesQuery.map((category) => category.id);
    const newFilters = { ...filters, categoryIds, page: 1 };
    setFilters(newFilters);
  };

  const handleAvailableFilter = (checked) => {
    const newFilters = { ...filters, available: checked, page: 1 };
    setFilters(newFilters);
  };

  const handleSetNewPageNumber = (number) => {
    const newFilters = { ...filters, page: number };
    setFilters(newFilters);
  };

  return (
    <>
      {detailsBookModalOpen && (
        <DetailsBookModal
          display={detailsBookModalOpen}
          handleClose={handleCloseBookModal}
          book={selectedBook}
          categories={categories}
          collection={collection}
        />
      )}
      <HelmetMetaData
        quote={t('sharing.quote')}
        title={t('sharing.title')}
        image="https://labibli.com/assets/images/image04.jpg?v=c75d1198"
        description={t('sharing.description')}
        hashtag={t('sharing.hashtag')}
      />
      <Container component="main" className={classes.paper}>
        {(notFound && !loading) ? (
          <Typography component="h1" variant="h6">
            {t('sharing.not_found')}
          </Typography>
        ) : (
          <Grid container spacing={4}>
            {/* ⛔ Pas de titre/intro ici. Il y en a eu un du 01/09 au 05/09/2026
              (`EnTetePage`, partagé avec les pages privées) : retiré à la
              demande de Séraphin, la mise en page ne convenait pas à la
              vitrine. Si un titre revient un jour, ne JAMAIS y poser
              `collection.organization` — voir
              memory/project_labibli_organisations_nom_genere.md : 22 des 31
              collections publiques portent encore un courriel privé dans ce
              nom, faute d'écran pour le changer. Ça a fuité le 01/09/2026,
              pendant deux heures. */}
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              className={classes.content}
            >
              <Search
                onSearch={handleSearch}
                setOnlyAvailableFilter={handleAvailableFilter}
                handleOnFilterCategories={handleOnFilterCategories}
                categories={categories}
              />
            </Grid>
            <Grid item xs={12}>
              {(emptyListFlag && !loading)
                            && (
                            <Typography type="p" variant="body1">
                              <span role="img" aria-label="red cross">❌</span>
                              {' '}
                              {t('search.no_book_found')}
                            </Typography>
                            )}
            </Grid>
            {loading
              ? spinner
              : (
                <Collection
                  books={books}
                  onBookClick={handleOpenBookModal}
                  collection={collection}
                  handleSetNewPageNumber={handleSetNewPageNumber}
                  page={filters.page}
                />
              )}
          </Grid>
        )}
      </Container>

    </>
  );
}
